// Path commands for the keyboard and the Properties panel (DRAW_PLAN §1 "Path operations", D3 part):
// Join (Ctrl+J), Average (Ctrl+Alt+J), Make / Release compound path (Ctrl+8 / Ctrl+Alt+Shift+8), Reverse
// direction, Simplify, and deleting selected anchors.
import { useDesign } from '../state';
import { useDesignUi } from './designUi';
import { currentList } from '../render/instance';
import { geometry } from '../render/displayList';
import { makeCompound, releaseCompound, setSubpaths, removeNodes } from '../commands/commands';
import { averageAnchors, deleteAnchor, joinSubpaths, reverseSubpath } from '../geometry/pathOps';
import { simplifySubpath } from '../geometry/fitCurve';
import { localDelta, toLocal, worldSubpaths } from '../tools/pathTarget';
import type { Mat2D } from '../model/types';

/** Drawable path / shape items behind the selection (groups contribute their contents). */
function selectedItems() {
  const list = currentList();
  const sel = new Set(useDesignUi.getState().selection);
  return list.items.filter(
    (it) => sel.has(it.topId) && !it.locked && (it.node.kind === 'path' || it.node.kind === 'shape'),
  );
}

/** Join: two open paths become one (nearest ends meet); a single open path closes. */
export function joinPaths() {
  const its = selectedItems();
  const st = useDesign.getState();
  if (its.length === 1) {
    const sps = geometry(its[0]!.node).subpaths;
    const k = sps.findIndex((s) => !s.closed);
    if (k >= 0)
      st.run(
        setSubpaths(
          its[0]!.id,
          sps.map((s, i) => (i === k ? joinSubpaths(s) : s)),
        ),
      );
    return;
  }
  if (its.length !== 2) return;
  const [a, b] = its as [(typeof its)[0], (typeof its)[0]];
  const wa = worldSubpaths(a).find((s) => !s.closed),
    wb = worldSubpaths(b).find((s) => !s.closed);
  if (!wa || !wb) return;
  const joined = joinSubpaths(wa, wb);
  st.run({ ...setSubpaths(a.id, toLocal(a, [joined])), merge: 'join' });
  st.run({ ...removeNodes([b.topId === b.id ? b.id : b.id]), merge: 'join' });
  st.endGesture();
  useDesignUi.getState().set({ selection: [a.topId] });
}

/** Average: the selected anchors (Direct selection) move to their average position. */
export function averageSelected() {
  const ui = useDesignUi.getState();
  const list = currentList();
  const refs = ui.anchors;
  if (refs.length < 2) return;
  const world = refs
    .map((r) => {
      const it = list.byId.get(r.id);
      return it ? worldSubpaths(it)[r.sp]?.points[r.i] : undefined;
    })
    .filter((p): p is NonNullable<typeof p> => !!p);
  const [ax, ay] = averageAnchors(world);
  const st = useDesign.getState();
  const byNode = new Map<string, typeof refs>();
  for (const r of refs) byNode.set(r.id, [...(byNode.get(r.id) ?? []), r]);
  for (const [id, rs] of byNode) {
    const it = list.byId.get(id)!;
    const w = worldSubpaths(it);
    const next = w.map((sp, s) => ({
      closed: sp.closed,
      points: sp.points.map((p, i) => {
        if (!rs.some((r) => r.sp === s && r.i === i)) return p;
        const dx = ax - p.x,
          dy = ay - p.y;
        return { ...p, x: ax, y: ay, inX: p.inX + dx, inY: p.inY + dy, outX: p.outX + dx, outY: p.outY + dy };
      }),
    }));
    st.run({ ...setSubpaths(id, toLocal(it, next)), merge: 'average' });
  }
  st.endGesture();
}

export function compound(make: boolean) {
  const st = useDesign.getState();
  const its = selectedItems();
  const out: string[] = [];
  if (make) {
    const worlds = new Map<string, Mat2D>(its.map((it) => [it.id, it.world]));
    // Only objects that are themselves in a layer (not inside groups) take part.
    const ids = its.filter((it) => it.topId === it.id).map((it) => it.id);
    st.run(makeCompound(ids, worlds, out));
  } else
    st.run(
      releaseCompound(
        its.map((it) => it.id),
        out,
      ),
    );
  if (out.length) useDesignUi.getState().set({ selection: out });
}

export function reversePaths() {
  const st = useDesign.getState();
  for (const it of selectedItems())
    st.run({ ...setSubpaths(it.id, geometry(it.node).subpaths.map(reverseSubpath)), merge: 'reverse' });
  st.endGesture();
}

/** Simplify within `tolerance` points (DRAW_PLAN §5). */
export function simplifyPaths(tolerance: number) {
  const st = useDesign.getState();
  for (const it of selectedItems()) {
    const w = worldSubpaths(it).map((sp) => simplifySubpath(sp, tolerance));
    st.run({ ...setSubpaths(it.id, toLocal(it, w)), merge: 'simplify' });
  }
  st.endGesture();
}

/** Deletes the anchors selected with Direct selection (a path left with fewer than 2 anchors goes). */
export function deleteSelectedAnchors(): boolean {
  const ui = useDesignUi.getState();
  if (!ui.anchors.length) return false;
  const list = currentList();
  const st = useDesign.getState();
  const byNode = new Map<string, { sp: number; i: number }[]>();
  for (const r of ui.anchors) byNode.set(r.id, [...(byNode.get(r.id) ?? []), r]);
  for (const [id, rs] of byNode) {
    const it = list.byId.get(id);
    if (!it) continue;
    let sps = geometry(it.node).subpaths.map((s) => ({ closed: s.closed, points: [...s.points] }));
    // Delete from the highest index down so indices stay valid.
    for (const r of [...rs].sort((a, b) => b.i - a.i)) sps[r.sp] = deleteAnchor(sps[r.sp]!, r.i);
    sps = sps.filter((s) => s.points.length >= 2);
    st.run({ ...(sps.length ? setSubpaths(id, sps) : removeNodes([id])), merge: 'delete-anchors' });
  }
  st.endGesture();
  ui.set({ anchors: [] });
  return true;
}

export { localDelta };
